import * as React from 'react';
import { useState } from 'react';
import { Typography, Grid2 as Grid, IconButton } from '@mui/material';
import { PlayArrowRounded } from '@mui/icons-material';
import { Swiper, SwiperSlide } from 'swiper/react';

import Heading from '../elements/heading';
import { TangibleResults } from '../../types/blocks/tangible-results';
import { ReviewsList } from '../elements/reviews-list';
import { TrustpilotWidget } from '../elements/trustpilot-widget';
import { VideoModal } from '../elements/video-modal';
import { commonSwiperConfig } from '@/shared/utils';

interface SectionTangibleResultsProps {
  element: TangibleResults;
}

export const SectionTangibleResults: React.FC<SectionTangibleResultsProps> = ({ element }) => {
  const [isMediaAtStart, setIsMediaAtStart] = useState(true);
  const [isMediaAtEnd, setIsMediaAtEnd] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{
    playbackId: string;
    playbackToken: string;
    title: string;
  } | null>(null);
  return (
    <>
      <div className="space-y-10 py-16 max-w-[1264px] mx-auto px-4 md:px-[40px] lg:px-[88px]">
        <Heading title={element.title} />
        <div className="space-y-2 lg:space-y-4">
          <Grid container spacing={{ xs: 1, md: 2 }}>
            {!!element.stats?.[0] && (
              <Grid size={{ xs: 12, md: 7, xl: 8 }}>
                <div className="h-full p-12 text-center bg-paper rounded-3xl">
                  <Typography variant="title-xl" component="p" className="mb-2">
                    {element.stats[0].value}
                  </Typography>
                  <Typography variant="h5" color="textSecondary">
                    {element.stats[0].label}
                  </Typography>
                </div>
              </Grid>
            )}
            {!!element.stats?.[1] && (
              <Grid size={{ xs: 12, md: 5, xl: 4 }}>
                <div className="p-12 text-center bg-paper rounded-3xl">
                  <Typography variant="title-xl" component="p" className="mb-2">
                    {element.stats[1].value}
                  </Typography>
                  <Typography variant="h5" color="textSecondary">
                    {element.stats[1].label}
                  </Typography>
                </div>
              </Grid>
            )}
          </Grid>

          {!!element.successStories?.[0] && (
            <div className="relative p-10 bg-paper rounded-3xl md:p-12">
              <div className="flex gap-4">
                <div className="flex-1 space-y-2">
                  <Typography variant="title-xl" component="h4">
                    {element.successStories[0].title}
                  </Typography>
                  <Typography variant="h5" color="textSecondary">
                    {element.successStories[0].description}
                  </Typography>
                </div>
                <img
                  src={element.successStories[0].icon?.url}
                  className="hidden md:block h-[148px] w-auto mx-12"
                />
              </div>

              <Swiper
                {...commonSwiperConfig}
                spaceBetween={8}
                slidesPerView={1.1}
                breakpoints={{
                  640: { slidesPerView: 1.5, spaceBetween: 16 },
                  1024: { slidesPerView: 3, spaceBetween: 32 },
                }}
                onSlideChange={(swiper) => {
                  setIsMediaAtStart(swiper.isBeginning);
                  setIsMediaAtEnd(swiper.isEnd);
                }}
                onReachBeginning={() => setIsMediaAtStart(true)}
                onReachEnd={() => setIsMediaAtEnd(true)}
                className="mt-8 lg:mt-14"
              >
                {!isMediaAtStart && (
                  <div className="absolute top-0 left-0 w-16 h-full bg-gradient-to-r from-[#15151A] bg-opacity-50 to-transparent z-10 pointer-events-none" />
                )}
                {!isMediaAtEnd && (
                  <div className="absolute top-0 right-0 w-16 h-full bg-gradient-to-l from-[#15151A] bg-opacity-50 to-transparent z-10 pointer-events-none" />
                )}
                {element.successStories[0].mediaList.map((media) => (
                  <SwiperSlide key={media.id}>
                    <div
                      className="relative overflow-hidden bg-cover rounded-4xl"
                      style={{ backgroundImage: `url('${media.thumbnail?.url}')` }}
                    >
                      <div className="absolute inset-0 bg-black bg-opacity-50" />
                      <div className="h-full relative z-10 flex flex-col text-center gap-y-8 lg:gap-y-20 min-h-[280px] sm:min-h-[350px] md:min-h-[400px] lg:h-[580px]">
                        <div className="flex items-end justify-center h-28 sm:h-36 lg:h-[50%]">
                          <IconButton
                            onClick={() =>
                              setSelectedVideo({
                                playbackId: media.muxPlaybackId,
                                playbackToken: media.muxPlaybackToken,
                                title: media.title,
                              })
                            }
                            size="large"
                            color="primary"
                            className="button-icon-contained button-icon-primary size-[55px] px-2 mx-auto"
                          >
                            <PlayArrowRounded />
                          </IconButton>
                        </div>
                        <div className="h-[50%] px-2 md:px-10 pb-12">
                          <Typography
                            variant="h3"
                            color="textPrimary"
                            className="break-words truncate text-wrap"
                          >
                            {media.title}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}

          {!!element.successStories?.[0] && (
            <div className="mt-12">
              <Swiper
                {...commonSwiperConfig}
                spaceBetween={16}
                slidesPerView={1.2}
                breakpoints={{
                  768: { slidesPerView: 1.5, slidesPerGroup: 1 },
                  1060: { slidesPerView: 2.5, slidesPerGroup: 2 },
                }}
              >
                {element.successStories[0].trustpilotReviews?.map((review, index) => (
                  <SwiperSlide key={index}>
                    <ReviewsList reviews={[review]} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}
        </div>
        <TrustpilotWidget />
      </div>
      {selectedVideo && (
        <VideoModal
          isOpen={!!selectedVideo}
          onClose={() => setSelectedVideo(null)}
          playbackId={selectedVideo.playbackId}
          playbackToken={selectedVideo.playbackToken}
        />
      )}
    </>
  );
};
